import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import djiBridge from '../utils/cloud-sample-DjiBridge';
import cloudModule, { EComponentName, EStatusValue } from '../utils/cloud-sample-cloudModule';
import { setAuth, clearAuth } from '../utils/auth';
import { ELocalStorageKey, API_ENDPOINTS } from '../utils/constants';

// DJI Configuration
const DJI_CONFIG = {
  appId: "159162",
  appKey: "b60f8b74980f0ce70bb7bbf7745e0d2",
  appLicense: "uYfI2feXZsct/MCi4/78VoBXikrOV33CfVBrJHoGjNsz9FA06vSDsbCKo+4CibUQ2IMEmqlRL6Y0qcHazP/yoXrW7GzGE8AZD7/w3g7U0sjVg3ekz1wINZ5M/dN4bL48hbQH8GrSihUGD9xefCV4w1sXm3nGN4k1wUHQIonYCZw="
};

/**
 * Verify DJI license - exactly as in pilot-index.vue
 */
const verifyLicense = () => {
  const isVerified = djiBridge.platformVerifyLicense(
    DJI_CONFIG.appId, 
    DJI_CONFIG.appKey, 
    DJI_CONFIG.appLicense
  ) && djiBridge.isPlatformVerifySuccess();
  
  if (isVerified) {
    console.log('[PILOT] The license verification is successful.');
  } else {
    console.error('[PILOT] Filed to verify the license. Please check license whether the license is correct, or apply again.');
  }
  
  return isVerified;
};

/**
 * PilotLogin Component
 * Provides user authentication and initializes the DJI JSBridge
 */
const PilotLogin = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('pilot');
  const [password, setPassword] = useState('pilot123');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isVerified, setIsVerified] = useState(false);

  // Check for existing authentication and verify license on mount
  useEffect(() => {
    // First, initialize loading state
    setLoading(true);
    
    // STEP 1: Verify license
    console.log('[PILOT] Verifying DJI license');
    const verified = verifyLicense();
    setIsVerified(verified);
    
    if (!verified) {
      setError('Please verify the license firstly.');
      setLoading(false);
      return;
    }
    
    // STEP 2: Set platform information
    console.log('[PILOT] Setting platform information');
    djiBridge.platformSetInformation('OpenSAR Platform', 'OpenSAR', 'Search and Rescue Operations');
    
    // STEP 3: Check localStorage for existing token
    console.log('[PILOT] Checking localStorage for existing token');
    const storedToken = localStorage.getItem(ELocalStorageKey.Token);
    
    if (storedToken) {
      console.log('[PILOT] Found token in localStorage:', storedToken.substring(0, 10) + '...');
      // STEP 4: If token exists, validate/refresh it
      validateExistingToken(storedToken);
    } else {
      // If no token, just wait for user to log in
      console.log('[PILOT] No existing token, waiting for login');
      setLoading(false);
    }
  }, [navigate]);

  // Validate an existing token by checking current user
  const validateExistingToken = async (token) => {
    try {
      // Set auth token in header
      const headers = {
        'x-auth-token': token
      };
      
      console.log('[PILOT] Validating existing token with user info request');
      console.log('[PILOT] Request URL:', API_ENDPOINTS.USER_INFO);
      
      // Make request to get current user info
      const response = await axios.get(API_ENDPOINTS.USER_INFO, { headers });
      console.log('[PILOT] User info response:', response.data);
      
      // Check if response is successful with code 0
      if (response.data && response.data.code === 0) {
        console.log('[PILOT] Token is valid, user info retrieved successfully');
        
        // Store token in auth utility
        setAuth(token, response.data.data);

        // Explicitly set the token in djiBridge
        console.log('[PILOT] Setting token in djiBridge:', token.substring(0, 10) + '...');
        djiBridge.setToken(token);
        
        // Store MQTT config from user info response
        if (response.data.data.mqttConfig) {
          localStorage.setItem(ELocalStorageKey.MqttConfig, JSON.stringify(response.data.data.mqttConfig));
          
          // Store WebSocket URL specifically for WS module
          if (response.data.data.mqttConfig.wsUrl) {
            localStorage.setItem(ELocalStorageKey.WsUrl, response.data.data.mqttConfig.wsUrl);
            console.log('[PILOT] Saved WebSocket URL to localStorage:', response.data.data.mqttConfig.wsUrl);
          }
        }
        
        // STEP 5: Define dummy global callbacks (required by djiBridge, even if not used)
        // This prevents "connectCallback is not defined" errors
        if (!window.connectCallback) {
          window.connectCallback = (arg) => {
            console.log('[PILOT] Dummy connectCallback called (ignored in login):', arg);
          };
        }
        if (!window.wsConnectCallback) {
          window.wsConnectCallback = (arg) => {
            console.log('[PILOT] Dummy wsConnectCallback called (ignored in login):', arg);
          };
        }
        
        // STEP 6: Set API component parameters
        console.log('[PILOT] Setting API component parameters with token');
        djiBridge.setComponentParam(EComponentName.Api, {
          host: window.location.origin,
          token: token
        });
        
        // STEP 7: Load API component
        console.log('[PILOT] Loading API component');
        const apiLoaded = djiBridge.loadComponent(
          EComponentName.Api, 
          djiBridge.getComponentParam(EComponentName.Api)
        );
        
        if (!apiLoaded) {
          console.error('[PILOT] Failed to load API component');
          setError('Failed to initialize API module');
          setLoading(false);
          return;
        }
        
        // Compare token and host values with what was sent
        const sentToken = token;
        const sentHost = window.location.origin;
        
        // Get current values from the bridge
        try {
          // Note: The JSBridge interface returns a JSON string that needs to be parsed
          const tokenResponse = djiBridge.getToken();
          console.log('[PILOT] Raw token response:', tokenResponse);
          const parsedTokenResponse = JSON.parse(tokenResponse);
          const currentToken = parsedTokenResponse.code === 0 ? parsedTokenResponse.data : null;
          
          const hostResponse = djiBridge.getHost();
          console.log('[PILOT] Raw host response:', hostResponse);
          const parsedHostResponse = JSON.parse(hostResponse);
          const currentHost = parsedHostResponse.code === 0 ? parsedHostResponse.data : null;
          
          // Log comparison results
          console.log('[PILOT] API Token Comparison:');
          console.log('[PILOT] Sent token:', sentToken ? `${sentToken.substring(0, 10)}...` : 'null');
          console.log('[PILOT] Current token from bridge:', currentToken ? `${currentToken.substring(0, 10)}...` : 'null');
          console.log('[PILOT] Token match:', sentToken === currentToken);
          
          console.log('[PILOT] API Host Comparison:');
          console.log('[PILOT] Sent host:', sentHost);
          console.log('[PILOT] Current host from bridge:', currentHost);
          console.log('[PILOT] Host match:', sentHost === currentHost);
        } catch (error) {
          console.error('[PILOT] Error comparing token and host values:', error);
        }
        
        // COMMENTED OUT: Thing component loading moved to PilotDashboard.js
        // Set up MQTT connection if mqttConfig is available
        /*
        const mqttConfigStr = localStorage.getItem(ELocalStorageKey.MqttConfig);
        if (mqttConfigStr) {
          try {
            const mqttConfig = JSON.parse(mqttConfigStr);
            console.log('[PILOT] Setting up MQTT/Thing component with stored config');
            
            // Create callback function for connection status with binding loop
            const mqttConnectCallback = (success) => {
              console.log('[PILOT] MQTT connection callback result:', success);
              localStorage.setItem('mqttConnected', success ? 'true' : 'false');
              
              // Start device binding loop when MQTT connects
              if (success) {
                console.log('[PILOT] MQTT connected, starting device binding loop');
                
                // Get controller SN from DJI bridge
                const gatewaySn = djiBridge.getRemoteControllerSN();
                console.log('[PILOT] Controller SN for binding:', gatewaySn);
                
                if (gatewaySn === EStatusValue.DISCONNECT.toString() || !gatewaySn) {
                  console.error('[PILOT] Cannot bind device - no controller SN available');
                  return;
                }
                
                // Prepare binding parameters
                const bindParam = {
                  device_sn: gatewaySn,
                  user_id: response.data.data.user_id || response.data.data.userId,
                  workspace_id: response.data.data.workspace?.workspace_id || response.data.data.workspace_id
                };
                
                console.log('[PILOT] Binding parameters:', {
                  device_sn: bindParam.device_sn,
                  user_id: bindParam.user_id ? bindParam.user_id.substring(0, 8) + '...' : 'null',
                  workspace_id: bindParam.workspace_id ? bindParam.workspace_id.substring(0, 8) + '...' : 'null'
                });
                
                // Start binding loop - attempt every 2 seconds until success
                const bindInterval = setInterval(() => {
                  if (!bindParam.device_sn || !bindParam.user_id || !bindParam.workspace_id) {
                    console.log('[PILOT] Binding parameters incomplete, skipping attempt');
                    return;
                  }
                  
                  console.log('[PILOT] Attempting to bind device:', bindParam.device_sn);
                  
                  const headers = { 'x-auth-token': token };
                  
                  axios.post(`/api/devices/${bindParam.device_sn}/binding`, bindParam, { headers })
                    .then(bindRes => {
                      console.log('[PILOT] Binding response:', bindRes.data);
                      if (bindRes.data.code !== 0) {
                        console.error('[PILOT] Binding failed:', bindRes.data.message);
                      } else {
                        console.log('[PILOT] ✅ Device bound successfully!');
                        clearInterval(bindInterval);
                      }
                    })
                    .catch(err => {
                      console.error('[PILOT] Binding request error:', err.message);
                      if (err.response) {
                        console.error('[PILOT] Error response:', err.response.data);
                      }
                    });
                }, 2000);
              }
            };
            
            // Configure MQTT component
            djiBridge.setComponentParam(EComponentName.Thing, {
              host: `${mqttConfig.server}:${mqttConfig.port}`,
              username: mqttConfig.username,
              password: mqttConfig.password,
              connectCallback: mqttConnectCallback
            });
            
            // Load the MQTT/Thing component
            console.log('[PILOT] Loading MQTT/Thing component');
            const thingLoaded = djiBridge.loadComponent(
              EComponentName.Thing,
              djiBridge.getComponentParam(EComponentName.Thing)
            );
            
            console.log('[PILOT] MQTT/Thing component loaded:', thingLoaded);
          } catch (error) {
            console.error('[PILOT] Error setting up MQTT component:', error);
          }
        }
        */
        
        // Navigate to dashboard since token is valid
        navigate('/dashboard');
      } else {
        console.log('[PILOT] Token validation failed: Status OK but code not 0');
        setError('Your session has expired. Please log in again.');
        localStorage.removeItem(ELocalStorageKey.Token);
        setLoading(false);
      }
    } catch (err) {
      console.error('[PILOT] Error validating token:', err);
      console.log('[PILOT] Error response:', err.response ? err.response.data : 'No response data');
      
      // If 401 unauthorized, token is expired
      if (err.response && err.response.status === 401) {
        console.log('[PILOT] Token expired (401), please log in');
        setError('Your session has expired. Please log in again.');
        localStorage.removeItem(ELocalStorageKey.Token);
      } else {
        setError('Error validating your session. Please try again.');
      }
      
      setLoading(false);
    }
  };

  // Handle login submission
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!isVerified) {
      setError('Please verify the license firstly.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Make login request
      const loginData = {
        username, 
        password
      };
      
      console.log('[PILOT] Making login request with data:', { ...loginData, password: '****' });
      console.log('[PILOT] Login URL:', API_ENDPOINTS.LOGIN);
      
      const response = await axios.post(API_ENDPOINTS.LOGIN, loginData);
      console.log('[PILOT] Login response:', response.data);
      
      // Check if login was successful (code 0)
      if (response.data && response.data.code === 0) {
        // Extract data from the response
        const data = response.data.data;
        
        // Get the token from response - check for both token and access_token
        const token = data.token || data.access_token;
        
        // Check if we have a token
        if (!token) {
          console.error('[PILOT] No token found in response');
          setError('Login successful but no token received');
          setLoading(false);
          return;
        }
        
        console.log('[PILOT] Obtained token:', token.substring(0, 10) + '...');
        
        // Store token in auth utility
        setAuth(token, data);
        
        // Store token and user data in localStorage
        localStorage.setItem(ELocalStorageKey.Token, token);
        localStorage.setItem(ELocalStorageKey.Username, data.username);
        localStorage.setItem(ELocalStorageKey.UserId, data.user_id || data.userId);
        
        // Store MQTT configuration
        if (data.mqttConfig) {
          localStorage.setItem(ELocalStorageKey.MqttConfig, JSON.stringify(data.mqttConfig));
          
          if (data.mqttConfig.wsUrl) {
            localStorage.setItem(ELocalStorageKey.WsUrl, data.mqttConfig.wsUrl);
            console.log('[PILOT] Saved WebSocket URL to localStorage:', data.mqttConfig.wsUrl);
          }
        }
        
        // Store workspace info if available
        if (data.workspace) {
          localStorage.setItem(ELocalStorageKey.WorkspaceId, data.workspace.workspace_id || '');
          localStorage.setItem(ELocalStorageKey.WorkspaceName, data.workspace.workspace_name || 'OpenSAR');
          localStorage.setItem(ELocalStorageKey.WorkspaceDesc, data.workspace.workspace_desc || 'Search and Rescue Operations');
          localStorage.setItem(ELocalStorageKey.PlatformName, data.workspace.platform_name || 'OpenSAR Platform');
        }
        
        try {
          // Explicitly set the token in djiBridge
          console.log('[PILOT] Setting token in djiBridge:', token.substring(0, 10) + '...');
          djiBridge.setToken(token);
          
          // Define dummy global callbacks (required by djiBridge, even if not used)
          // This prevents "connectCallback is not defined" errors
          if (!window.connectCallback) {
            window.connectCallback = (arg) => {
              console.log('[PILOT] Dummy connectCallback called (ignored in login):', arg);
            };
          }
          if (!window.wsConnectCallback) {
            window.wsConnectCallback = (arg) => {
              console.log('[PILOT] Dummy wsConnectCallback called (ignored in login):', arg);
            };
          }
          
          // STEP 5: Set API component parameters
          console.log('[PILOT] Setting API component parameters with token');
          djiBridge.setComponentParam(EComponentName.Api, {
            host: window.location.origin,
            token: token
          });
          
          // STEP 6: Load API component
          console.log('[PILOT] Loading API component');
          const apiLoaded = djiBridge.loadComponent(
            EComponentName.Api, 
            djiBridge.getComponentParam(EComponentName.Api)
          );
          
          if (!apiLoaded) {
            console.error('[PILOT] Failed to load API component');
            setError('Failed to initialize API module');
            setLoading(false);
            return;
          }
          
          // Set platform information with data from server
          const platformResult = djiBridge.platformSetInformation(
            data.workspace?.platform_name || 'OpenSAR Platform',
            data.workspace?.workspace_name || 'OpenSAR',
            data.workspace?.workspace_desc || 'Search and Rescue Operations'
          );
          console.log('[PILOT] Platform information set result:', platformResult);
          
          // COMMENTED OUT: Thing component loading moved to PilotDashboard.js
          // Set up MQTT connection if mqttConfig is available
          /*
          if (data.mqttConfig) {
            console.log('[PILOT] Setting up MQTT/Thing component');
            
            // Create callback function for connection status with binding loop
            const mqttConnectCallback = (success) => {
              console.log('[PILOT] MQTT connection callback result:', success);
              localStorage.setItem('mqttConnected', success ? 'true' : 'false');
              
              // Start device binding loop when MQTT connects
              if (success) {
                console.log('[PILOT] MQTT connected, starting device binding loop');
                
                // Get controller SN from DJI bridge
                const gatewaySn = djiBridge.getRemoteControllerSN();
                console.log('[PILOT] Controller SN for binding:', gatewaySn);
                
                if (gatewaySn === EStatusValue.DISCONNECT.toString() || !gatewaySn) {
                  console.error('[PILOT] Cannot bind device - no controller SN available');
                  return;
                }
                
                // Prepare binding parameters
                const bindParam = {
                  device_sn: gatewaySn,
                  user_id: data.user_id || data.userId,
                  workspace_id: data.workspace?.workspace_id || data.workspace_id
                };
                
                console.log('[PILOT] Binding parameters:', {
                  device_sn: bindParam.device_sn,
                  user_id: bindParam.user_id ? bindParam.user_id.substring(0, 8) + '...' : 'null',
                  workspace_id: bindParam.workspace_id ? bindParam.workspace_id.substring(0, 8) + '...' : 'null'
                });
                
                // Start binding loop - attempt every 2 seconds until success
                const bindInterval = setInterval(() => {
                  if (!bindParam.device_sn || !bindParam.user_id || !bindParam.workspace_id) {
                    console.log('[PILOT] Binding parameters incomplete, skipping attempt');
                    return;
                  }
                  
                  console.log('[PILOT] Attempting to bind device:', bindParam.device_sn);
                  
                  const headers = { 'x-auth-token': token };
                  
                  axios.post(`/api/devices/${bindParam.device_sn}/binding`, bindParam, { headers })
                    .then(bindRes => {
                      console.log('[PILOT] Binding response:', bindRes.data);
                      if (bindRes.data.code !== 0) {
                        console.error('[PILOT] Binding failed:', bindRes.data.message);
                      } else {
                        console.log('[PILOT] ✅ Device bound successfully!');
                        clearInterval(bindInterval);
                      }
                    })
                    .catch(err => {
                      console.error('[PILOT] Binding request error:', err.message);
                      if (err.response) {
                        console.error('[PILOT] Error response:', err.response.data);
                      }
                    });
                }, 2000);
              }
            };
            
            // Configure MQTT component
            djiBridge.setComponentParam(EComponentName.Thing, {
              host: `${data.mqttConfig.server}:${data.mqttConfig.port}`,
              username: data.mqttConfig.username,
              password: data.mqttConfig.password,
              connectCallback: mqttConnectCallback
            });
            
            // Load the MQTT/Thing component
            console.log('[PILOT] Loading MQTT/Thing component');
            const thingLoaded = djiBridge.loadComponent(
              EComponentName.Thing,
              djiBridge.getComponentParam(EComponentName.Thing)
            );
            
            console.log('[PILOT] MQTT/Thing component loaded:', thingLoaded);
          }
          */
          
          // Navigate to dashboard
          setLoading(false);
          navigate('/dashboard');
        } catch (error) {
          console.error('[PILOT] Error setting up API component:', error);
          setError('Failed to initialize API module: ' + error.message);
          setLoading(false);
        }
      } else {
        console.error('[PILOT] Login failed with code:', response.data.code);
        setError(response.data.message || 'Login failed');
        setLoading(false);
      }
    } catch (err) {
      console.error('[PILOT] Login error:', err);
      console.log('[PILOT] Error response:', err.response ? err.response.data : 'No response data');
      setError(err.response?.data?.message || err.message || 'Login failed');
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#f5f7fa',
      fontFamily: "'Inter', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
    }}>
      <div style={{
        width: '100%',
        maxWidth: '420px',
        backgroundColor: '#ffffff',
        border: '1px solid #cfd8dc',
        padding: '32px'
      }}>
          <h2 style={{
            textAlign: 'center',
            marginBottom: '8px',
            fontWeight: 700,
            color: '#1565c0',
            fontSize: '1.5rem'
          }}>OpenSAR Pilot</h2>
          <p style={{
            textAlign: 'center',
            marginBottom: '24px',
            color: '#546e7a',
            fontSize: '0.875rem'
          }}>Search and Rescue Operations</p>

          {error && <Alert variant="danger">{error}</Alert>}

          <Form onSubmit={handleLogin}>
            <Form.Group className="mb-3">
              <Form.Label style={{ color: '#1a2332', fontWeight: 500, fontSize: '0.875rem' }}>Username</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
                style={{ borderRadius: '0', borderColor: '#cfd8dc', fontSize: '0.875rem' }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ color: '#1a2332', fontWeight: 500, fontSize: '0.875rem' }}>Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                style={{ borderRadius: '0', borderColor: '#cfd8dc', fontSize: '0.875rem' }}
              />
            </Form.Group>

            <Button
              type="submit"
              className="w-100"
              disabled={loading || !isVerified}
              style={{
                backgroundColor: '#1565c0',
                borderColor: '#1565c0',
                borderRadius: '0',
                fontWeight: 600,
                padding: '10px',
                fontSize: '0.875rem'
              }}
            >
              {loading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  <span className="ms-2">Logging in...</span>
                </>
              ) : 'Login'}
            </Button>
          </Form>
      </div>
    </div>
  );
};

export default PilotLogin; 